// How the fields of a product card read, shared by the result card and the
// result table so that the two show the same words.

import { CardMaker, ProductCard } from "../../types/productPlane";

// Shown in place of the name of a product no source names
export const UNNAMED_PRODUCT = "Unnamed product";

// A fixed code of the export as words: "in_service" reads "in service"
export const formatCode = (code: string | null): string =>
  code ? code.replace(/_/g, " ") : "";

// What a product no source names is called: by its curated class where it
// has one ("Unnamed inertial navigation system"), else "Unnamed product". A
// class label that starts with a capital reads in lower case after
// "Unnamed", unless its first word is an acronym ("HVAC unit").
export const unnamedName = (category: string | null | undefined): string => {
  if (!category) {
    return UNNAMED_PRODUCT;
  }
  const label = /^[A-Z][a-z]/.test(category)
    ? category.charAt(0).toLowerCase() + category.slice(1)
    : category;
  return `Unnamed ${label}`;
};

export const productName = (product: ProductCard): string =>
  product.name ?? unnamedName(product.category?.name);

// "level · status", or the one of the two the product has
export const productLevelAndStatus = (product: ProductCard): string =>
  [product.level, product.status]
    .map(formatCode)
    .filter(Boolean)
    .join(" · ");

// "+4 more" for the capabilities a card does not name; empty for none
export const moreCapabilities = (product: ProductCard): string =>
  product.more_capabilities > 0 ? `+${product.more_capabilities} more` : "";

// Shown in place of the name of an organization no source names
export const UNNAMED_ORGANIZATION = "Unnamed organization";

// One part of the makers' line: what the organizations do, and who they are
export interface MakerGroup {
  label: string;
  names: string[];
}

// Who makes the product, as the card's one line says it: "Made by A, B",
// "developed by C", or "Made & developed by A" when the same organizations
// do both. Empty for a product with no maker or developer on record.
export const makerGroups = (product: ProductCard): MakerGroup[] => {
  const named = (role: "manufacturer" | "developer"): string[] =>
    product.makers
      .filter((maker) => maker.roles.includes(role))
      .map((maker) => maker.organization.name ?? UNNAMED_ORGANIZATION);
  const made = named("manufacturer");
  const developed = named("developer");
  if (made.length > 0 && made.join("\n") === developed.join("\n")) {
    return [{ label: "Made & developed by", names: made }];
  }
  return [
    ...(made.length > 0 ? [{ label: "Made by", names: made }] : []),
    ...(developed.length > 0
      ? [{ label: made.length > 0 ? "developed by" : "Developed by", names: developed }]
      : []),
  ];
};

// A maker's part, as its chip on a card says it after its name
export const makerRole = (maker: CardMaker): string => {
  const makes = maker.roles.includes("manufacturer");
  const develops = maker.roles.includes("developer");
  return makes && develops ? "maker & developer" : makes ? "maker" : "developer";
};

// The makers' line in full, for its tooltip: every group, then the count of
// the organizations it does not name
export const makerLine = (product: ProductCard): string =>
  [
    ...makerGroups(product).map(
      (group) => `${group.label} ${group.names.join(", ")}`,
    ),
    ...(product.more_makers > 0 ? [`+${product.more_makers} more`] : []),
  ].join(" · ");
